import { useMemo } from 'react'
import Edit from '@mui/icons-material/Edit'
import {
  Button,
  Divider,
  Grid2,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import { useGetOrganization } from '@/service-library/hooks/organizations'
import {
  useGetVendorSettings,
  useGetLiveData,
} from '@/services/hooks/relay/dynamic'
import { LiveListEntity } from '@/types/live-lists'
import { BaseVendor } from '@/types/relay'
import {
  ENTRATA_VENDOR_ADDRESS_INFO_TYPE_IDS,
  ENTRATA_VENDOR_HEADER_ADDRESS_INFO_TYPE_IDS,
  GL_ACCOUNT_INFO_TYPES_IDS,
  INVOICE_REMIT_ADDRESS_INFO_TYPE_ID,
  PAYMENT_METHOD_INFO_TYPES_IDS,
  VENDOR_ADDRESS_INFO_TYPES_IDS,
  VENDOR_NAME_INFO_TYPES_IDS,
} from '@/utils/info-types'
import { useCredentialTypeContext } from '@/components/integration-settings/CredentialTypeProvider'
import VendorAliasesTable from '@/components/integration-settings/VendorAliasesTable'
import { useProjectGridFieldsContext } from '@/components/validation/ProjectGridFieldsProvider'
import useGetValueData from '@/components/validation/useGetValueData'
import EditVendorSettingsDialog from './EditVendorSettingsDialog'
import VendorCardFieldValue from './FieldValue'

type VendorSettingsProps = {
  orgId: string
  vendor: LiveListEntity<BaseVendor>
}

const DEFAULT_REMIT_ADDRESS_INFO_TYPE_IDS = [INVOICE_REMIT_ADDRESS_INFO_TYPE_ID]

const VENDOR_NAME_PUBLIC_AND_ENTRATA_INFO_TYPES_IDS = [
  ...VENDOR_NAME_INFO_TYPES_IDS,
  ...ENTRATA_VENDOR_ADDRESS_INFO_TYPE_IDS,
]

const isEquivalentAliasValue = (
  aliasValue?: string | null,
  currentValue?: string | null,
) => {
  if (!aliasValue && !currentValue) return true
  return aliasValue === currentValue
}

export default function VendorSettings({ orgId, vendor }: VendorSettingsProps) {
  const editOverlay = useOverlay()
  const fields = useProjectGridFieldsContext()
  const credentialType = useCredentialTypeContext()
  const allowedConfiguration = credentialType.allowed_configuration
  const isEntrataCredential = credentialType.code === 'entrata_api_token'

  const vendorAddressInfoTypeIds = isEntrataCredential
    ? ENTRATA_VENDOR_HEADER_ADDRESS_INFO_TYPE_IDS
    : VENDOR_ADDRESS_INFO_TYPES_IDS
  const remitAddressInfoTypeIds = isEntrataCredential
    ? ENTRATA_VENDOR_ADDRESS_INFO_TYPE_IDS
    : DEFAULT_REMIT_ADDRESS_INFO_TYPE_IDS

  const getValueData = useGetValueData()

  const { vendorSettings, queryKey } = useGetVendorSettings({
    orgId,
    vendorId: vendor.entity_id,
    orgCredentialTypeId: credentialType.id,
  })

  const { organization } = useGetOrganization({
    id: vendorSettings?.org_id || '',
    enabled: vendorSettings && vendorSettings.org_id !== orgId,
  })

  const { allData: paymentMethods, isPending: isPendingPaymentMethods } =
    // NOTE: IF entrata supports this field, we'd need to update to include info type
    useGetLiveData({
      infoTypeId: PAYMENT_METHOD_INFO_TYPES_IDS[0], // Since it's used as a hint, it's okay to pass any of them
      orgId: vendorSettings?.org_id || '',
      orgCredentialTypeId: credentialType.id,
      filters: {
        id: vendorSettings?.payment_method_id || '',
      },
      enabled:
        allowedConfiguration.includes('vendor_payment') &&
        !!vendorSettings?.payment_method_id,
    })

  const { allData: accounts, isPending: isPendingAccounts } = useGetLiveData({
    // NOTE: IF entrata supports this field, we'd need to update to include info type
    infoTypeId: GL_ACCOUNT_INFO_TYPES_IDS[0], // Since it's used as a hint, it's okay to pass any of them
    orgId: vendorSettings?.org_id || '',
    orgCredentialTypeId: credentialType.id,
    filters: {
      id: vendorSettings?.default_gl_id || '',
    },
    enabled:
      (allowedConfiguration.includes('vendor_gl') ||
        credentialType.supports.sync_default_gls) &&
      !!vendorSettings?.default_gl_id,
  })

  const { vendorNameValue, remitAddressValue, vendorAddressValue } =
    useMemo(() => {
      if (!allowedConfiguration.includes('vendor_alias')) {
        return {}
      }
      const vendorNameField = fields.find((field) =>
        VENDOR_NAME_PUBLIC_AND_ENTRATA_INFO_TYPES_IDS.includes(
          field.info_type_id || '',
        ),
      )
      const remitAddressField = fields.find((field) =>
        remitAddressInfoTypeIds.includes(field.info_type_id || ''),
      )
      const vendorAddressField = fields.find((field) =>
        vendorAddressInfoTypeIds.includes(field.info_type_id || ''),
      )

      const { value: vendorNameValue } = getValueData({
        fieldId: vendorNameField?.id,
      })
      const { value: remitAddressValue } = getValueData({
        fieldId: remitAddressField?.id,
      })
      const { value: vendorAddressValue } = getValueData({
        fieldId: vendorAddressField?.id,
      })
      return { vendorNameValue, remitAddressValue, vendorAddressValue }
    }, [
      allowedConfiguration,
      fields,
      getValueData,
      remitAddressInfoTypeIds,
      vendorAddressInfoTypeIds,
    ])

  const aliasWithCurrentDataIsIncluded =
    vendorSettings?.aliases?.some(
      (alias) =>
        isEquivalentAliasValue(alias.remit_address, remitAddressValue) &&
        isEquivalentAliasValue(alias.vendor_address, vendorAddressValue) &&
        isEquivalentAliasValue(alias.vendor_name, vendorNameValue),
    ) || false

  const addDefaultValues = useMemo(
    () => ({
      vendor_name: vendorNameValue || '',
      remit_address: remitAddressValue || '',
      vendor_address: vendorAddressValue || '',
    }),
    [vendorNameValue, remitAddressValue, vendorAddressValue],
  )

  const hasVendorFieldsConfiguration =
    allowedConfiguration.includes('vendor_roll_up') ||
    allowedConfiguration.includes('vendor_payment') ||
    allowedConfiguration.includes('vendor_gl')
  const showEditButton = vendorSettings && hasVendorFieldsConfiguration

  return (
    <Stack spacing={2}>
      <Divider />
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        justifyContent="space-between"
      >
        <Typography variant="body2">
          Vendor Settings
          {organization && (
            <Typography component="span" color="text.secondary" variant="body2">
              {' '}
              - Using Credential for {organization?.name}
            </Typography>
          )}
        </Typography>
        {showEditButton && (
          <>
            <Button
              size="small"
              startIcon={<Edit fontSize="inherit" />}
              variant="text"
              onClick={editOverlay.open}
            >
              Edit
            </Button>
            <EditVendorSettingsDialog
              allowedConfiguration={allowedConfiguration}
              vendorSettings={vendorSettings}
              queryKey={queryKey}
              overlay={editOverlay}
            />
          </>
        )}
      </Stack>
      {vendorSettings ? (
        <>
          {(hasVendorFieldsConfiguration ||
            credentialType.supports.sync_default_gls) && (
            <Grid2 container>
              {allowedConfiguration.includes('vendor_roll_up') && (
                <VendorCardFieldValue
                  field="Roll Up Line Items"
                  value={vendorSettings.roll_up_line_items ? 'Yes' : 'No'}
                />
              )}
              {allowedConfiguration.includes('vendor_payment') && (
                <VendorCardFieldValue
                  field="Payment Method"
                  value={
                    vendorSettings.payment_method_id &&
                    isPendingPaymentMethods ? (
                      <Skeleton width={100} />
                    ) : (
                      paymentMethods[0]?.pd_display_name
                    )
                  }
                />
              )}
              {(allowedConfiguration.includes('vendor_gl') ||
                credentialType.supports.sync_default_gls) && (
                <VendorCardFieldValue
                  field="Default GL Account"
                  value={
                    vendorSettings.default_gl_id && isPendingAccounts ? (
                      <Skeleton width={100} />
                    ) : (
                      accounts[0]?.pd_display_name
                    )
                  }
                />
              )}
            </Grid2>
          )}
          {allowedConfiguration.includes('vendor_alias') && (
            <VendorAliasesTable
              inValidationOverlay
              queryKey={queryKey}
              vendor={vendor}
              aliases={vendorSettings.aliases}
              orgId={vendorSettings.org_id}
              canEdit
              disabledSaveHelperText={
                aliasWithCurrentDataIsIncluded
                  ? 'An alias already exists with this information.'
                  : undefined
              }
              addDefaultValues={addDefaultValues}
            />
          )}
        </>
      ) : (
        <Skeleton />
      )}
    </Stack>
  )
}
